import { createSelector } from 'reselect'
import { isAccommodationItem, isBedbankAccommodationItemView, isBedbankItem, isLEHotelAccommodationItemView, isLEHotelItem, isVillaItem } from 'lib/checkout/checkoutUtils'
import { isNonEmptyArray, min, nonNullable, sum, unique } from 'lib/array/arrayUtils'
import { getExperienceBookingViewCartData, getExperienceListKey, mapExperienceToBookingView, getTargetAudience, getExperienceBookingViewCartDataFromTransferItems } from 'lib/experiences/experienceUtils'
import config from 'constants/config'
import { EmptyExperienceList, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_WATER_TRANSFER_ID } from 'constants/experience'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { checkoutAccommodationOfferView, getAccommodationEndDate, getAccommodationItems, getAccommodationStartDate } from './view/accommodation'
import { getFullOffers } from 'selectors/offerSelectors'
import { getVillaItems } from 'checkout/selectors/view/villa'
import { getMerchantFeeByOrderItem } from 'lib/payment/paymentUtils'
import { isExperienceItemUpcoming, isTransferItemUpcoming } from 'lib/order/isUpcoming'
import { EXPERIENCE_TRANSFER_ID } from '../../client/constants/experience'
import { isEmptyObject } from 'lib/object/objectUtils'
import { getDefaultTransfers } from 'checkout/Components/Experiences/ExperienceCheckoutTransferBookingModal'
import { findBestTransfer } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import { hasFijiHotelInCart } from './hasFijiHotelInCart'
import { ExperienceListFilters } from 'actions/ExperienceActions'
import getExperienceItems from './view/getExperienceItems'
import getTransferItems from './view/getTransferItems'

export const getLeOfferInCart = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (items, leOffers): App.Offer | undefined => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isLEHotelItem(item)) {
      return leOffers?.[item.offerId]
    }
  },
)

export const getVillaOfferInCart = createSelector(
  (state: App.State) => getVillaItems(state),
  (state: App.State) => state.offer.offers,
  (items, villaOffers): App.Offer | undefined => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isVillaItem(item)) {
      return villaOffers?.[item.offerId]
    }
  },
)

export const getBedbankOfferInCart = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getFullOffers(state),
  (items, offers): App.BedbankOffer | undefined => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isBedbankItem(item)) {
      return offers?.[item.offerId] as App.BedbankOffer
    }
  },
)

export const getHotelInCart = createSelector(
  (state: App.State) => getLeOfferInCart(state),
  (state: App.State) => getVillaOfferInCart(state),
  (state: App.State) => getBedbankOfferInCart(state),
  (leOffer, villaOffer, bedbankOffer) => leOffer ?? bedbankOffer ?? villaOffer,
)

export const getExperiencesEnabled = createSelector(
  (state: App.State) => getLeOfferInCart(state),
  (state: App.State) => getVillaOfferInCart(state),
  (state: App.State) => getBedbankOfferInCart(state),
  (offer, villaOffer, bedBankOffer) => {
    if (offer) {
      return config.EXPERIENCES_ENABLED && offer.experiencesInFlow
    }

    if (villaOffer) {
      return config.EXPERIENCES_ENABLED && villaOffer.experiencesInFlow
    }

    if (bedBankOffer) {
      return config.EXPERIENCES_ENABLED && config.EXPERIENCES_BEDBANK_INFLOW_ENABLED
    }

    return config.EXPERIENCES_ENABLED
  },
)

export const getCartOfferGeoCoords = createSelector(
  (state: App.State) => getHotelInCart(state),
  (offer) => {
    if (offer) {
      return {
        latitude: offer.property?.latitude,
        longitude: offer.property?.longitude,
      }
    } else {
      return {}
    }
  },
)

export const getCartOfferLocation = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (items, leOffers, bedbankOffers) => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isLEHotelItem(item)) {
      const offer = leOffers?.[item.offerId]
      return offer?.location ?? ''
    }
    if (item && isBedbankItem(item)) {
      const offer = bedbankOffers?.[item.offerId]
      const property = offer?.property
      if (property) {
        return [property.address.city, property.address.stateProvinceName].filter(Boolean).join(', ')
      }
    }
    return ''
  },
)

export const getCheckoutExperienceDateRange = createSelector(
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (from, to) => {
    return {
      from: from ? moment(from).add(-7, 'days').format(ISO_DATE_FORMAT) : undefined,
      to: to ? moment(to).add(7, 'days').format(ISO_DATE_FORMAT) : undefined,
    }
  },
)

export type ExperiencesCheckoutTopOfferListId = 'TRANSFER' |
  'WATER_TRANSFER' |
  'LOUNGE' |
  'HOTEL_CURATED' |
  'REGIONAL_CURATED' |
  'DISTANCE_SHORT' |
  'DISTANCE_MEDIUM' |
  'DISTANCE_LONG' |
  'FALLBACK'

export type ExperiencesCheckoutTopOfferListFilters = {
  key: string
  filters: ExperienceListFilters
  id: ExperiencesCheckoutTopOfferListId
}

export const getTopOfferListFilters = createSelector(
  (state: App.State) => getCartOfferGeoCoords(state),
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => getLeOfferInCart(state),
  (state: App.State) => getCheckoutExperienceDateRange(state),
  (state: App.State) => state.checkout.cart.items.filter(isAccommodationItem),
  (geoCoords, currencyCode, hotelOffer, availabilityParams, accommodationItems) => {
    if (!geoCoords.latitude || !geoCoords.longitude) return []
    const listFilters: Array<ExperiencesCheckoutTopOfferListFilters> = []

    function addToList(id: ExperiencesCheckoutTopOfferListId, filters: ExperienceListFilters) {
      // Disabling this feature temporarily
      // if (hotelOffer?.showOnlyExperiencesCurated && filters.offerId !== hotelOffer.id) return;
      const key = getExperienceListKey(filters)
      listFilters.push({ key, filters, id })
    }

    // Build target audience based on hotel booked
    const adults = accommodationItems[0]?.occupancy?.adults
    const children = accommodationItems[0]?.occupancy?.children
    const targetAudience = getTargetAudience(adults ?? 1, children)

    // Priority 1: Transfers & Water Transfers & Lounges
    addToList('TRANSFER', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['0KM', '50KM'],
      categoryCodes: [EXP_CATEGORY_AIRPORT_TRANSFER_ID],
      limit: 1,
      ...availabilityParams,
    })

    addToList('WATER_TRANSFER', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['0KM', '50KM'],
      categoryCodes: [EXP_CATEGORY_WATER_TRANSFER_ID],
      limit: 1,
      ...availabilityParams,
    })

    addToList('LOUNGE', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['0KM', '50KM'],
      categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID],
      limit: 1,
      ...availabilityParams,
    })

    // Priority 2: Hotel Offer Curated List
    if (hotelOffer?.experiencesCurated) {
      addToList('HOTEL_CURATED', {
        currencyCode,
        offerId: hotelOffer?.id,
        showUnlisted: true,
        limit: 2,
        ...availabilityParams,
      })
    }

    // Priority 3: Regional Offer Curated List
    addToList('REGIONAL_CURATED', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      onlyCuratedByRegion: true,
      limit: 3,
      ...availabilityParams,
    })

    // Priority 4: Distance based lists
    // Short distance:Two offers within 0-5 km → Tag: "Best deals near you."
    addToList('DISTANCE_SHORT', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['0KM', '5KM'],
      targetAudienceIn: [targetAudience],
      categoryCodesNotIn: [EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_WATER_TRANSFER_ID],
      limit: 2,
      ...availabilityParams,
    })

    // Medium distance: Two offers within 5-20 km → Tag: "Middle distance, 80% of customers buy it."
    addToList('DISTANCE_MEDIUM', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['5KM', '20KM'],
      targetAudienceIn: [targetAudience],
      categoryCodesNotIn: [EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_WATER_TRANSFER_ID],
      limit: 2,
      ...availabilityParams,
    })

    // Long distance: Two offers within 20-50 km → Tag: "You can't miss this."
    addToList('DISTANCE_LONG', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['20KM', '50KM'],
      targetAudienceIn: [targetAudience],
      categoryCodesNotIn: [EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_WATER_TRANSFER_ID],
      limit: 2,
      ...availabilityParams,
    })

    // Priority 5: Fallback List
    addToList('FALLBACK', {
      currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['0KM', '50KM'],
      targetAudienceIn: [targetAudience],
      categoryCodesNotIn: [EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_WATER_TRANSFER_ID],
      limit: 30,
      ...availabilityParams,
    })

    return listFilters
  },
)

export const getExperienceOfferList = createSelector(
  (state: App.State) => state.experience.experienceLists,
  (state: App.State) => getHotelInCart(state),
  (state: App.State) => getExperiencesEnabled(state),
  (state: App.State) => getTopOfferListFilters(state),
  (lists, hotelOffer, isExperienceEnabled, filters): App.ApiCallState<{ ids: Array<string> }> => {
    if (!hotelOffer) return { fetching: true, ids: [] }
    if (!isExperienceEnabled) return { fetching: false, ids: [] }

    const newLists = filters.map(filter => (lists[filter.key] ?? EmptyExperienceList))

    return {
      fetching: isExperienceEnabled && newLists.some(list => list.fetching),
      ids: unique(newLists.flatMap(list => list.ids)),
    }
  },
)

export const getExperiencesViews = createSelector(
  (state: App.State) => state.experience.experiences,
  (state: App.State) => getHotelInCart(state),
  (state: App.State) => getBedbankOfferInCart(state),
  (experiences, offer, bedbankOffer): Array<App.ExperienceBookingView> => {
    return Object.values(experiences).filter(Boolean).map(exp =>
      mapExperienceToBookingView(exp, undefined, offer?.property ?? bedbankOffer?.property),
    )
  },
)

export const getAllPurchasableExperiences = createSelector(
  (state: App.State) => getExperiencesViews(state),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getTransferItems(state),
  (experienceViews, cartExperienceItems, cartTransferItems): Array<App.ExperienceBookingView> => {
    return experienceViews
      .map(item => {
        const experienceItem = cartExperienceItems?.find(i => i.experienceId === item.id)
        const transferItems = cartTransferItems?.filter(i => i.experienceId === item.id)
        let cartData: App.ExperienceBookingView['cartData']

        if (experienceItem) {
          cartData = getExperienceBookingViewCartData(experienceItem)
        } else if (transferItems.length) {
          cartData = getExperienceBookingViewCartDataFromTransferItems(transferItems)
        }

        return {
          ...item,
          added: !!cartData?.count,
          cartData,
        }
      })
  },
)

export const getTopOffersInflow = createSelector(
  (state: App.State) => getExperienceOfferList(state),
  (state: App.State) => getAllPurchasableExperiences(state),
  (topOfferList, allExperiences) => {
    // Keep the order of items from topOfferList
    const orderedExperiences = nonNullable(topOfferList.ids.map(id => allExperiences.find(exp => exp.id === id)))
    return orderedExperiences
  },
)

export const getTopOffersInFlowWithoutTransfer = createSelector(
  (state: App.State) => getTopOffersInflow(state),
  (experiences) => experiences.filter(experience => {
    const withNoCategory = !experience.categories.length
    const withNoTransferCategory = !experience.categories.some(category => category.id === EXPERIENCE_TRANSFER_ID)
    const isNotSetTransfer = !experience.features?.transfer

    return withNoCategory || (withNoTransferCategory && isNotSetTransfer)
  }),
)

export const getExperienceRefundDetails = createSelector(
  (state: App.State) => state.experience.refundDetails,
  (state: App.State) => state.experience.cancellingExperiences,
  (state: App.State, order: App.Order) => order,
  (experiencesRefundDetails, cancellingExperiences, order) => {
    let total = 0
    let itemToCancel = { amount: 0, fee: 0 }

    const refundDetails = Object.values(experiencesRefundDetails)

    if (refundDetails?.length) {
      total = sum(refundDetails.map(refund => {
        const merchantFeeItem = getMerchantFeeByOrderItem(order.merchantFees, refund.orderItemId)
        const refundableMerchantFee = merchantFeeItem?.shouldAutoRefund ? merchantFeeItem?.amount : 0
        return refund.refundAmount + refundableMerchantFee
      }))
    }

    if (refundDetails?.length && cancellingExperiences?.length) {
      const items = refundDetails.filter(refund => cancellingExperiences.some(item => item.id === refund.orderItemId))
      itemToCancel = {
        amount: sum(items.map(refund => {
          const merchantFeeItem = getMerchantFeeByOrderItem(order.merchantFees, refund.orderItemId)
          const refundableMerchantFee = merchantFeeItem?.shouldAutoRefund ? merchantFeeItem?.amount : 0
          return refund.refundAmount + refundableMerchantFee
        })),
        fee: sum(items.map(refund => {
          const merchantFeeItem = getMerchantFeeByOrderItem(order.merchantFees, refund.orderItemId)
          const nonRefundableMerchantFee = merchantFeeItem && !merchantFeeItem.shouldAutoRefund ? merchantFeeItem?.amount : 0
          return refund.refundFee + nonRefundableMerchantFee
        })),
      }
    }

    return {
      total,
      itemToCancel,
    }
  },
)

export const getChangeDatesExperiencesOutsideHotelStay = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (order, startDate, endDate): Array<App.OrderExperienceItem> => {
    if (!order) return []
    const experiences = order.experienceItems.filter((e) => isExperienceItemUpcoming(e, order))

    return experiences.filter(item => {
      if (startDate && endDate && moment(item.date).isBetween(startDate, endDate, null, '[]')) {
        return false
      }

      if (item.complimentary) {
        return false
      }

      return true
    })
  },
)

export const getChangeDatesTransfersOutsideHotelStay = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (order, startDate, endDate): Array<App.OrderTransferItem> => {
    if (!order) return []

    const transfers = order.transferItems.filter(isTransferItemUpcoming)

    return transfers.filter(item => {
      if (startDate && endDate && moment(item.date).isBetween(startDate, endDate, null, '[]')) {
        return false
      }

      if (item.complimentary) {
        return false
      }

      return true
    })
  },
)

export const getOneClickExperienceTransfer = createSelector(
  (state: App.State) => getTopOffersInflow(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.experience.experiences,
  (purchasableExperiences, checkoutTransferItems, allExperiences): App.ExperienceBookingView | undefined => {
    if (isNonEmptyArray(checkoutTransferItems) && !isEmptyObject(allExperiences)) {
      const experienceTransferId = checkoutTransferItems[0].experienceId
      const experienceTransferOffer = allExperiences[experienceTransferId]
      return mapExperienceToBookingView(experienceTransferOffer)
    }

    const transfers = Object.values(purchasableExperiences)?.filter(experience => experience.categories.some(category => category.id === EXPERIENCE_TRANSFER_ID))
    if (!transfers.length) return

    const preferredTransfers = transfers.filter(transfer => !!transfer.features?.transfer?.preferredTransfer)
    if (preferredTransfers.length) {
      return min(preferredTransfers, (transfer) => transfer.price)
    }

    return min(transfers, (transfer) => transfer.price)
  },
)

export const isAccomodationWithNoIncludedTransfer = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.checkout.cart.items,
  (accommodationOfferView, checkoutItems) => {
    const containsAccommodation = checkoutItems.some(item => isLEHotelItem(item) || isBedbankItem(item))
    if (!containsAccommodation) return false

    const containsIncludedTransfer = accommodationOfferView.data.some((offerView) => {
      return !!offerView.itemViews.some(itemView => {
        if (isBedbankAccommodationItemView(itemView)) {
          const inclusions = itemView.inclusions ?? []
          return inclusions.some(
            inclusion => inclusion.description.toLocaleLowerCase().includes('transfer'),
          )
        }

        if (isLEHotelAccommodationItemView(itemView)) {
          const inclusions = itemView.inclusions ?? []
          const bonusInclusions = itemView?.bonusInclusions ?? []
          return [...inclusions, ...bonusInclusions].some(
            inclusion => inclusion.parentCategory === 'Transport' ||
              inclusion.description.toLocaleLowerCase().includes('transfer'),
          )
        }

        return false
      })
    })

    return !containsIncludedTransfer
  },
)

/**
 * One click transfer is enabled when:
 * - There is a transfer available for the accommodation location
 * - The user has an accommodation in the cart
 * - The accommodation doesn't have a transfer included in the offer
 * - The accomodation is not located in Fiji due to logistical complications
 *   In this region, transfers require both a vehicle and a water ferry, making the process less straightforward
 */
export const isOneClickExperienceTransferEnabled = createSelector(
  (state: App.State) => getOneClickExperienceTransfer(state),
  (state: App.State) => isAccomodationWithNoIncludedTransfer(state),
  (state: App.State) => hasFijiHotelInCart(state),
  (state: App.State) => getExperiencesEnabled(state),
  (oneClickTransfer, isAccomodationWithNoIncludedTransfer, hasFijiHotelInCart, isExperienceEnabled) =>
    !!oneClickTransfer && isAccomodationWithNoIncludedTransfer && !hasFijiHotelInCart && isExperienceEnabled,
)

export const getExperienceTransferBestOptions = createSelector(
  (state: App.State) => state.experience.experienceTransferOptions,
  (state: App.State) => getDefaultTransfers(state, getOneClickExperienceTransfer(state)),
  (transferOptions, defaultTransfers) => {
    const [toHotelOptions, toAirportOptions] = [
      Object.values(transferOptions).flatMap(option => option?.transfers ?? []).filter(transfer => transfer?.transferTypes.includes('AIRPORT-TO-HOTEL')),
      Object.values(transferOptions).flatMap(option => option?.transfers ?? []).filter(transfer => transfer?.transferTypes.includes('HOTEL-TO-AIRPORT')),
    ]

    const { bestTransfer: bestToHotelOption } = findBestTransfer(defaultTransfers.toHotel, toHotelOptions)
    const { bestTransfer: bestToAirportOption } = findBestTransfer(defaultTransfers.toAirport, toAirportOptions)

    return {
      bestToHotelOption,
      bestToAirportOption,
    }
  },
)
